<script setup>
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import TextField from '@/components/Form/TextField.vue';
import { timestampToTime, formatToLocalDate } from '@/helpers';
import { useChatStore } from '@/stores/chat';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute } from 'vue-router';

const chat = useChatStore()
const { getMessages } = storeToRefs(chat)

const form = ref({
    content: '',
    files: []
})

const messages = computed(() =>
  [...getMessages.value].sort((a, b) => a.createdAt - b.createdAt)
);

const lastMessageEl = ref(null);
let observer = null;
// only activate read-tracking when on “ChatPage”
const route = useRoute();
watch(() => route.name, (newName) => {
  if (newName == 'chat') {
    startObserving();
  } else {
    stopObserving();
  }
});
//  After messages render, hook up an IntersectionObserver on the last message
// Re-find the latest bubble whenever the number of messages changes. Watching the
// length (not a deep watch over the whole array) avoids re-traversing megabytes of
// image data on every store mutation.
watch(
  () => getMessages.value.length,
  async () => {
    // A new message landed (most likely the echo of the one we just sent) — clear the loader.
    stopSending();
    await nextTick();
    const els = document.querySelectorAll('.chat-MessageBubble');
    lastMessageEl.value = els[0] || null;
    startObserving();
  }
);

const fileInput = ref(null);
const files = ref([]);
const zoomedImage = ref(null);

// Shows a spinner on the submit button between hitting send and the server
// echoing the message back over the socket (can take a few seconds for big images).
const sending = ref(false);
let sendingTimeout = null;
const stopSending = () => {
    sending.value = false;
    if (sendingTimeout) { clearTimeout(sendingTimeout); sendingTimeout = null; }
};

// The original file is sent over the socket, so cap it below the server's
// payload limit (handshake reports maxPayload ≈ 30 MB).
const MAX_INPUT_BYTES = 25 * 1024 * 1024; // 25 MB
const MAX_DIMENSION = 1280;               // longest edge after resize
const JPEG_QUALITY = 0.8;

// Decode → downscale on a canvas → re-encode as JPEG. Used ONLY to build a small
// preview/thumbnail for the UI. The original file is what we actually send, so the
// stored image stays full quality; only the on-screen decode is made cheap.
const compressImage = async (file) => {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    return blob || file;
};

const handleFileUpload = async () => {
    const uploadedFiles = fileInput.value?.files;
    if (!uploadedFiles) return;

    for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        if (file.size > MAX_INPUT_BYTES) {
            alert(`Image is too large (max ${Math.round(MAX_INPUT_BYTES / (1024 * 1024))} MB).`);
            continue;
        }
        // Build a small, display-only preview so the UI never decodes the full image.
        // Fall back to the original if compression fails.
        let previewUrl;
        try {
            const previewBlob = await compressImage(file);
            previewUrl = URL.createObjectURL(previewBlob);
        } catch (err) {
            console.warn('Preview compression failed, using original for preview', err);
            previewUrl = URL.createObjectURL(file);
        }
        files.value.push({
            file,            // ORIGINAL binary — sent to the server at full quality
            previewUrl,      // compressed, display-only
            name: file.name,
            size: file.size,
            type: file.type,
        });
    }
    // allow re-selecting the same file
    if (fileInput.value) fileInput.value.value = '';
};
const removeFile = (index) => {
    const removed = files.value.splice(index, 1)[0];
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
}

const openImage = (url) => {
    zoomedImage.value = url;
};

const closeImage = () => {
    zoomedImage.value = null;
};

const submit = async () => {
    if (sending.value) { return }
    if (files.value.length == 0 && form.value.content == '') { return }

    sending.value = true;
    // Fallback so the button never gets stuck if the echo never arrives.
    if (sendingTimeout) clearTimeout(sendingTimeout);
    sendingTimeout = setTimeout(stopSending, 20000);

    if (files.value.length) {
        // Backend reads the binary `file`. Send the ORIGINAL file (full quality), no base64 url.
        form.value.files = files.value.map(fileData => ({
            file: fileData.file,
            name: fileData.name,
            size: fileData.size,
            type: fileData.type,
        }));
    }
    chat.setMessage(form.value)

    files.value.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    form.value.content = ''
    form.value.files = []
    files.value = []
}

const height = computed(() => {
    const headerHeight = document.getElementById("header")?.offsetHeight
    return Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - headerHeight
})

onMounted(() => {
    chat.fetchMessages()
    if (route.name === 'chat') {
    startObserving();
  }
})
onBeforeUnmount(() => {
  stopObserving();
  if (sendingTimeout) clearTimeout(sendingTimeout);
});
 function startObserving() {
  if (route.name !== 'chat' || !lastMessageEl.value) return;
  stopObserving();
  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          //  send markAllRead  to backend
          chat.markAllRead();
          stopObserving();
        }
      });
    },
    {
      root: document.querySelector('.overflow-y-auto'),
      threshold: 0.8,
    }
  );

  observer.observe(lastMessageEl.value);
}

function stopObserving() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}
</script>

<template>
    <div>
        <div class="flex flex-col bg-[var(--color-grey6)]" :style="{maxHeight: height + 'px'}">
            <div class="px-4 py-2 flex-1 flex flex-col-reverse overflow-y-auto">
                <div 
                    v-for="message in messages"
                    :class="[message.opid ? 'left' : 'right']"
                    class="chat-MessageBubble"
                >       
                    <div v-if="zoomedImage" class="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" @click="closeImage">
                        <img :src="zoomedImage" class="max-w-full max-h-full rounded-md shadow-lg" />
                    </div>

                    <div v-if="message.files?.length" class="flex flex-col gap-4 justify-end pb-1"   :class="[message.opid ? 'items-start' : 'items-end']"> 
                        <img v-for="file in message.files" :src="file?.url" class="w-32 h-20 object-cover cursor-pointer rounded-md" loading="lazy" decoding="async" @click="openImage(file.url)" />
                    </div>
                    <p class="chat-MessageContent">{{ message.content }}</p>
                    <p class="chat-MessageTime">
                        <span> {{ formatToLocalDate(message.createdAt) }} </span>
                        <span> {{ timestampToTime(message.createdAt) }} </span>
                    </p>
                </div>
            </div>
            <form @submit.prevent="submit" class="px-4 py-2 flex space-x-2 items-end">
                <div class="flex-1">
                    <!-- Uploaded Images Preview -->
                    <div class="pb-1 flex items-center gap-2">
                        <div v-for="(file, index) in files" :key="index" class="relative">
                            <img :src="file.previewUrl" class="w-12 h-12 rounded object-cover" decoding="async" />
                            <button 
                                @click="removeFile(index)" 
                                class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                ×
                            </button>
                        </div>
                    </div>
                    <!-- Input Field -->
                    <div class="input-wrapper">
                        <TextField class="w-full h-9 font-normal text-sm pr-10" v-model="form.content" :placeholder="$t('general.20')" />
                        <label for="fileInput" class="clip-icon">
                            <img src="/clip.svg" class="w-5 h-5"/>
                            <input id="fileInput" type="file" accept="image/png, image/jpeg" style="display: none" ref="fileInput" @change="handleFileUpload">
                        </label>
                    </div>
                </div>
                <DefaultButton type="submit" :loading="sending">{{$t('app.1')}}</DefaultButton>
            </form>
        </div>
    </div>
</template>

<style scoped>
.chat-MessageBubble {
    @apply px-4 py-1 rounded-md shadow-md w-max max-w-80 mb-2  break-words;
}

.chat-MessageBubble.left {
    @apply bg-white text-left;
}

.chat-MessageBubble.right {
    @apply bg-emerald-300 text-right ml-auto;
}

.chat-MessageContent {
    @apply text-sm;
}

.chat-MessageTime {
    @apply text-xs text-neutral-600 flex items-center gap-2;
}
.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.clip-icon {
    position: absolute;
    right: 10px;
    bottom: 50%;
    transform: translateY(50%);
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
}

.clip-icon:hover {
    opacity: 1;
}


</style>